import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  type TextNode,
} from 'lexical';
import {
  formulaBodyText,
  isInsideTextLiteral,
  lexFormula,
  resolveTable,
  TABLE_FUNCTIONS,
  type Span,
} from '@erp/workbooks';
import {
  $createFormulaReferenceNode,
  $createFormulaTableReferenceNode,
} from './formulaReferenceNode';
import type { FormulaFields } from './worksheetFormulaFields';

/** A run of Formula text, or one reference the editor shows as a chip. */
export type FormulaSegment =
  | { kind: 'text'; text: string }
  | { kind: 'reference'; name: string }
  | { kind: 'table'; tableId: string; column: string | null };

type ChipSegment = Exclude<FormulaSegment, { kind: 'text' }>;

/**
 * The references in Formula text that become chips, with their spans: Column
 * and Row references, Qualified references to this Worksheet's input, and
 * Table references naming one of `fields.tables`. A Qualified reference to
 * another input, or a Table reference naming no table, stays text, so its
 * Diagnostic shows. `typing` leaves out a whole table named without a column
 * (VLOOKUP's `Prices`): a column may still be typed after it.
 */
function chipSpans(
  text: string,
  fields: FormulaFields,
  typing = false,
): { chip: ChipSegment; span: Span }[] {
  const input = fields.inputName.toLowerCase();
  const tables = fields.tables ?? [];
  return lexFormula(text).flatMap(
    (token): { chip: ChipSegment; span: Span }[] => {
      if (
        token.kind === 'column' ||
        (token.kind === 'qualified' && token.qualifier.toLowerCase() === input)
      )
        return [
          { chip: { kind: 'reference', name: token.name }, span: token.span },
        ];
      if (token.kind !== 'table' || (typing && token.column === null))
        return [];
      const resolved = resolveTable(tables, token.table);
      return resolved.ok
        ? [
            {
              chip: {
                kind: 'table',
                tableId: resolved.item.id,
                column: token.column,
              },
              span: token.span,
            },
          ]
        : [];
    },
  );
}

/** Formula text (without its leading `=`) split into text and references. */
export function formulaSegments(
  text: string,
  fields: FormulaFields,
): FormulaSegment[] {
  const segments: FormulaSegment[] = [];
  let offset = 0;
  for (const { chip, span } of chipSpans(text, fields)) {
    if (span.start > offset)
      segments.push({ kind: 'text', text: text.slice(offset, span.start) });
    segments.push(chip);
    offset = span.end;
  }
  if (offset < text.length)
    segments.push({ kind: 'text', text: text.slice(offset) });
  return segments;
}

function $createChip(chip: ChipSegment) {
  return chip.kind === 'reference'
    ? $createFormulaReferenceNode(chip.name)
    : $createFormulaTableReferenceNode(chip.tableId, chip.column);
}

/** Replaces the editor's content with a stored Formula. */
export function $setFormula(expression: string, fields: FormulaFields): void {
  const paragraph = $createParagraphNode();
  for (const segment of formulaSegments(expression, fields))
    paragraph.append(
      segment.kind === 'text'
        ? $createTextNode(segment.text)
        : $createChip(segment),
    );
  $getRoot().clear().append(paragraph);
}

/**
 * The Formula as text, every chip written as its Row reference and every Table
 * reference in stored form (by id).
 */
export function $formulaText(): string {
  return $getRoot().getTextContent();
}

/**
 * The selection as the text a spreadsheet reads, for the clipboard: Row
 * references, and Table references by their tables' current names.
 */
export function $copiedFormulaText(fields: FormulaFields): string | null {
  const selection = $getSelection();
  if (!selection) return null;
  return formulaBodyText(selection.getTextContent(), fields.tables ?? []);
}

/**
 * Turns the first complete reference typed or pasted into `node` into a chip.
 * The text is read in the context of its siblings, so a reference inside a text
 * literal stays text. The transform runs again on the remainder.
 */
export function $chipTypedReference(
  node: TextNode,
  fields: FormulaFields,
): void {
  if (!node.isSimpleText()) return;
  const before = node
    .getPreviousSiblings()
    .map((sibling) => sibling.getTextContent())
    .join('');
  const own = node.getTextContent();
  const after = node
    .getNextSiblings()
    .map((sibling) => sibling.getTextContent())
    .join('');
  const found = chipSpans(before + own + after, fields, true).find(
    ({ span }) =>
      span.start >= before.length && span.end <= before.length + own.length,
  );
  if (!found) return;
  const start = found.span.start - before.length;
  const end = found.span.end - before.length;
  const selection = $getSelection();
  const caretAtEnd =
    $isRangeSelection(selection) &&
    selection.isCollapsed() &&
    selection.anchor.key === node.getKey() &&
    selection.anchor.offset === end;
  const parts = node.splitText(start, end);
  const target = start === 0 ? parts[0] : parts[1];
  const chip = $createChip(found.chip);
  target?.replace(chip);
  if (caretAtEnd) chip.selectNext(0, 0);
}

const TRIGGER = /(\[@?|@)([^[\]@"]{0,75})$/;

/**
 * Opens the reference picker on `@`, `[` or `[@` typed outside a text literal,
 * with the text typed after it as the query. `text` is the current text run up
 * to the caret.
 */
export function formulaReferenceTrigger(text: string): {
  leadOffset: number;
  matchingString: string;
  replaceableString: string;
} | null {
  const match = TRIGGER.exec(text);
  if (!match || isInsideTextLiteral(text, match.index)) return null;
  return {
    leadOffset: match.index,
    matchingString: match[2] ?? '',
    replaceableString: match[0],
  };
}

/** Whether the text before the caret opens the reference picker. */
export function $isPickerTriggered(): boolean {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;
  const { anchor } = selection;
  const node = anchor.getNode();
  return (
    anchor.type === 'text' &&
    formulaReferenceTrigger(node.getTextContent().slice(0, anchor.offset)) !==
      null
  );
}

/** The Formula text before the caret, chips included. */
export function $textBeforeCaret(): string {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return '';
  const { anchor } = selection;
  const node = anchor.getNode();
  const before = node
    .getPreviousSiblings()
    .map((sibling) => sibling.getTextContent())
    .join('');
  return anchor.type === 'text'
    ? before + node.getTextContent().slice(0, anchor.offset)
    : before;
}

/**
 * Whether `text` ends inside the inputs of a function that reads another
 * table (XLOOKUP, VLOOKUP, SUMIFS, COUNTIFS), at any depth.
 */
export function isInsideLookup(text: string): boolean {
  const calls: (string | null)[] = [];
  let name: string | null = null;
  for (const token of lexFormula(text)) {
    if (token.kind === 'symbol' && token.value === '(') calls.push(name);
    else if (token.kind === 'symbol' && token.value === ')') calls.pop();
    name = token.kind === 'name' ? token.value : null;
  }
  return calls.some(
    (call) => call !== null && Object.hasOwn(TABLE_FUNCTIONS, call),
  );
}
